
/*
  FLIPROAD — CART DRAWER
  Carrito lateral compatible con el tema Shopify.

  Funciones:
  - Abrir y cerrar carrito lateral
  - Actualizar contenido tras añadir productos
  - Actualizar icono del carrito
  - Mantener cantidades y eliminación con cart.js
  - Compatibilidad con el carrito personalizado FlipRoad
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.isCartUpdating = false;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', () => {
        this.close();
      });
    }

    this.setHeaderCartIconAccessibility();
  }

  /*
    ABRIR CARRITO LATERAL
  */

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.classList.remove('is-empty');

    const items = this.querySelector('cart-drawer-items');

    if (items && items.classList.contains('is-empty')) {
      this.classList.add('is-empty');
    }

    this.classList.add('animate');

    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    document.body.classList.add('overflow-hidden');

    const dialog = this.querySelector('.drawer__inner');

    if (dialog) {
      dialog.setAttribute('aria-modal', 'true');

      const focusTarget =
        dialog.querySelector('.drawer__close') || dialog;

      requestAnimationFrame(() => {
        if (
          typeof trapFocus === 'function' &&
          this.classList.contains('active')
        ) {
          try {
            trapFocus(dialog, focusTarget);
          } catch (error) {
            focusTarget.focus();
          }
        } else {
          focusTarget.focus();
        }
      });
    }

    this.initializeUpsellButtons();
    this.initializeVariantSelectors();
  }

  /*
    CERRAR CARRITO
  */

  close() {
    this.classList.remove('active');

    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    const dialog = this.querySelector('.drawer__inner');

    if (dialog) {
      dialog.removeAttribute('aria-modal');
    }
  }

  /*
    ACCESIBILIDAD DEL ICONO DEL CARRITO
  */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) {
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (
        event.code === 'Space' ||
        event.code === 'SPACE' ||
        event.key === ' '
      ) {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /*
    ACTUALIZAR CARRITO DESPUES DE AÑADIR UN PACK

    Recibe la respuesta de /cart/add.js con
    la sección "cart-drawer" ya renderizada.
  */

  renderContents(parsedState) {
    if (
      !parsedState ||
      !parsedState.sections ||
      !parsedState.sections['cart-drawer']
    ) {
      console.error(
        'FlipRoad: Shopify no devolvió la sección cart-drawer.'
      );
      return false;
    }

    const sectionHTML = parsedState.sections['cart-drawer'];

    const parsedDocument = new DOMParser().parseFromString(
      sectionHTML,
      'text/html'
    );

    const newInner = parsedDocument.querySelector(
      '.drawer__inner'
    );

    const currentInner = this.querySelector(
      '.drawer__inner'
    );

    if (!newInner || !currentInner) {
      console.error(
        'FlipRoad: no se encuentra el contenido del carrito.'
      );
      return false;
    }

    /*
      Reemplazamos todo el contenido interno:
      productos, contador, envío, subtotal
      y botón de checkout.

      Así no dependemos de que el footer
      exista previamente cuando el carrito
      estaba vacío.
    */

    currentInner.innerHTML = newInner.innerHTML;

    this.classList.toggle(
      'is-empty',
      newInner.querySelector('cart-drawer-items.is-empty') !== null
    );

    /*
      Actualizamos el icono del carrito
      si Shopify ha devuelto su sección.
    */

    if (parsedState.sections['cart-icon-bubble']) {
      this.updateCartIcon(
        parsedState.sections['cart-icon-bubble']
      );
    }

    this.initializeUpsellButtons();
    this.initializeVariantSelectors();

    this.open();

    this.dispatchEvent(
      new CustomEvent('cartUpdated', {
        bubbles: true,
        detail: {
          cartData: parsedState
        }
      })
    );

    return true;
  }

  /*
    ACTUALIZAR ICONO SIN PERDER EVENTOS
  */

  updateCartIcon(sectionHTML) {
    const currentIcon = document.querySelector(
      '#cart-icon-bubble'
    );

    if (!currentIcon) {
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(
      sectionHTML,
      'text/html'
    );

    const newIcon = parsedDocument.querySelector(
      '#cart-icon-bubble'
    );

    if (!newIcon) {
      return;
    }

    /*
      Conservamos el elemento original
      para mantener su evento de apertura.
    */

    currentIcon.innerHTML = newIcon.innerHTML;
  }

  /*
    SECCIONES QUE DEBE DEVOLVER SHOPIFY
  */

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      }
    ];

    if (document.querySelector('#cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    return sections;
  }

  /*
    OBTENER CONTENIDO HTML
  */

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) {
      return '';
    }

    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = parsedDocument.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  /*
    GUARDAR ELEMENTO QUE ABRIO EL CARRITO
  */

  setActiveElement(element) {
    this.activeElement = element;
  }

  /*
    ACTUALIZAR CARRITO DESDE SHOPIFY
    Utilizado también por productos adicionales.
  */

  async updateCart() {
    if (this.isCartUpdating) {
      return;
    }

    this.isCartUpdating = true;

    try {
      const baseUrl =
        (window.routes && routes.cart_url) || '/cart';

      const response = await fetch(
        baseUrl + '?section_id=cart-drawer',
        {
          headers: {
            'Accept': 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error('No se pudo actualizar el carrito.');
      }

      const html = await response.text();

      const parsedDocument = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const newInner = parsedDocument.querySelector(
        '.drawer__inner'
      );

      const currentInner = this.querySelector(
        '.drawer__inner'
      );

      if (!newInner || !currentInner) {
        throw new Error(
          'No se encontró el contenido actualizado.'
        );
      }

      currentInner.innerHTML = newInner.innerHTML;

      this.classList.toggle(
        'is-empty',
        newInner.querySelector('cart-drawer-items.is-empty') !== null
      );

      this.initializeUpsellButtons();
      this.initializeVariantSelectors();

      /*
        Actualizar contador del icono
        usando la sección independiente.
      */

      if (document.querySelector('#cart-icon-bubble')) {
        const iconResponse = await fetch(
          baseUrl + '?section_id=cart-icon-bubble'
        );

        if (iconResponse.ok) {
          const iconHTML = await iconResponse.text();
          this.updateCartIcon(iconHTML);
        }
      }

      this.dispatchEvent(
        new CustomEvent('cartUpdated', {
          bubbles: true
        })
      );

    } catch (error) {
      console.error(
        'FlipRoad: error al actualizar el carrito.',
        error
      );
    } finally {
      this.isCartUpdating = false;
    }
  }

  /*
    AÑADIR PRODUCTOS ADICIONALES
    Compatibilidad con upsells del tema.
  */

  async addProductToCart(
    productId,
    variantId,
    isUpsell = false
  ) {
    const id = variantId || productId;

    if (!id || this.isCartUpdating) {
      return;
    }

    this.isCartUpdating = true;

    try {
      const addUrl =
        (window.routes && routes.cart_add_url) ||
        '/cart/add';

      const response = await fetch(
        addUrl + '.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: Number(id),
            quantity: 1
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'No se pudo añadir el producto.'
        );
      }

      this.dispatchEvent(
        new CustomEvent('cartUpdated', {
          bubbles: true,
          detail: {
            cartData: data
          }
        })
      );

    } catch (error) {
      console.error(
        'FlipRoad: error al añadir producto.',
        error
      );
    } finally {
      this.isCartUpdating = false;
    }

    await this.updateCart();

    if (!this.classList.contains('active')) {
      this.open();
    }
  }

  /*
    PRODUCTOS RECOMENDADOS
    Solo se utiliza si existen en el tema.
  */

  initializeUpsellButtons() {
    this.querySelectorAll('.upsell-add-button').forEach(
      (button) => {
        if (button.dataset.frBound === 'true') {
          return;
        }

        button.dataset.frBound = 'true';

        button.addEventListener('click', (event) => {
          event.preventDefault();

          const id = button.getAttribute(
            'data-product-id'
          );

          if (id) {
            this.addProductToCart(id, null, true);
          }
        });
      }
    );
  }

  /*
    SELECTORES DE VARIANTES PARA UPSELL
  */

  initializeVariantSelectors() {
    this.querySelectorAll(
      '.upsell-product__variant-selector'
    ).forEach((selector) => {
      if (selector.dataset.frBound === 'true') {
        return;
      }

      selector.dataset.frBound = 'true';

      selector.addEventListener('change', () => {
        const container = selector.closest(
          '.upsell-product'
        );

        if (!container) {
          return;
        }

        const button = container.querySelector(
          '.upsell-add-button'
        );

        if (button) {
          button.setAttribute(
            'data-product-id',
            selector.value
          );
        }

        const image = container.querySelector(
          '.upsell-product-image'
        );

        const selectedOption =
          selector.options[selector.selectedIndex];

        if (image && selectedOption) {
          const imageUrl = selectedOption.getAttribute(
            'data-image-url'
          );

          if (imageUrl && !imageUrl.includes('no-image')) {
            image.src = imageUrl;
          }
        }
      });
    });
  }
}

/*
  REGISTRAR COMPONENTE CART DRAWER
*/

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/*
  ELEMENTOS DEL CARRITO LATERAL

  Compatibilidad con Assets/cart.js:
  actualizar cantidades, precios e icono.
*/

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      }
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    return sections;
  }
}

/*
  REGISTRAR COMPONENTE
*/

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}
